import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useProductStore } from '../../../store/useProduct';
import { useLoginStore } from '../../../store/useLogin';
import LazyImage from '../../form/LazyImage';
import PDThumbnail from './detail/PDThumbnail';
import PDPrice from './detail/PDPrice';
import PDDescription from './detail/PDDescription';
import PDService from './detail/PDService';
import PDSampleLink from './detail/PDSampleLink';
import PDButtonWrapper from './detail/PDButtonWrapper';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faInfoCircle, faFileAlt, faWonSign, faClock, faEye } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';

/**
 * @author taulmax
 * @createDate 2024.10.08
 * @updateDate 2024.10.22
 * @description 상품 상세 페이지
 */
const ProductDetail = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedImage, setSelectedImage] = useState<number>(0);
  const [showPreviewModal, setShowPreviewModal] = useState<boolean>(false);
  const [previewPdfUrl, setPreviewPdfUrl] = useState<string>('');
  const [isPdfLoading, setIsPdfLoading] = useState<boolean>(false);

  const { productDetail, getProductDetail, updateProduct } = useProductStore();
  const { role } = useLoginStore();
  const [isUpdatingDisplay, setIsUpdatingDisplay] = useState<boolean>(false);

  const handlePreview = async () => {
    if (!productDetail) return;

    // sampleFile이 없거나 fileFullUrl이 없는 경우 처리
    if (!productDetail.sampleFile?.fileFullUrl) {
      toast.error('샘플 파일이 등록되지 않은 상품입니다.');
      return;
    }

    try {
      setShowPreviewModal(true);
      setIsPdfLoading(true);

      // PDF URL이 있는 경우
      const pdfUrl = productDetail.sampleFile.fileFullUrl;

      // PDF 미리 로드를 위한 prefetch
      const link = document.createElement('link');
      link.rel = 'prefetch';
      link.href = pdfUrl;
      document.head.appendChild(link);

      // 약간의 지연 후 URL 설정 (prefetch가 시작되도록)
      setTimeout(() => {
        setPreviewPdfUrl(pdfUrl);
      }, 100);
    } catch (error) {
      console.error('미리보기 오류:', error);
      toast.error('미리보기를 불러오는데 실패했습니다.');
      setShowPreviewModal(false);
      setIsPdfLoading(false);
    }
  };

  useEffect(() => {
    const productId = location.pathname.replaceAll('/product/', '');
    getProductDetail(productId);
  }, [getProductDetail, location]);

  // 선택된 이미지 URL 가져오기 함수
  const getSelectedImageUrl = (): string => {
    if (!productDetail) return '';

    if (selectedImage === 1) {
      return productDetail.inlineFile.fileFullUrl;
    } else if (selectedImage === 2) {
      return productDetail.endFile?.fileFullUrl || '';
    } else if (selectedImage >= 3) {
      const detailIndex = selectedImage - 3;
      return productDetail.detailFiles?.[detailIndex]?.fileFullUrl || productDetail.thumbnailFile.fileFullUrl;
    }

    return productDetail.thumbnailFile.fileFullUrl;
  };

  // PDF 미리 로드 (상품 정보가 로드되면)
  useEffect(() => {
    if (productDetail?.sampleFile?.fileFullUrl) {
      const link = document.createElement('link');
      link.rel = 'prefetch';
      link.href = productDetail.sampleFile.fileFullUrl;
      link.as = 'document';
      document.head.appendChild(link);

      return () => {
        if (document.head.contains(link)) {
          document.head.removeChild(link);
        }
      };
    }
  }, [productDetail?.sampleFile?.fileFullUrl]);

  return (
    <div>
      {/* 상단 네비게이션 */}
      <div className="border-b">
        <div className="max-w-5xl mx-auto px-4 py-3">
          <button 
            onClick={() => navigate('/product')}
            className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="w-3 h-3" />
            <span>상품 목록으로 돌아가기</span>
          </button>
        </div>
      </div>

      {/* 메인 콘텐츠 */}
      <div>
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* 이미지 영역 */}
            <div className="bg-white rounded-lg p-5 h-fit">
              <div className="aspect-square rounded-lg overflow-hidden bg-gray-50 mb-3" style={{ aspectRatio: '4/3' }}>
              {productDetail ? (
                <LazyImage
                  src={getSelectedImageUrl()}
                  alt={productDetail.productName}
                  className="w-full h-full object-contain"
                />
              ) : (
                <div className="w-full h-full animate-pulse bg-gray-200"></div>
              )}
            </div>
              {/* 이미지 썸네일 */}
              {productDetail && (
                <div className="space-y-3">
                  <div className="grid grid-cols-1 lg:grid-cols-4">
                    {/* 표지 이미지 */}
                    <div>
                      <p className="text-xs font-medium text-gray-600 mb-2">표지 이미지</p>
                      <button
                        onClick={() => setSelectedImage(0)}
                        className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                          selectedImage === 0 ? 'border-blue-500 shadow-md' : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <LazyImage
                          src={productDetail.thumbnailFile.fileFullUrl}
                          alt="thumbnail"
                          className="w-full h-full object-cover"
                        />
                      </button>
                    </div>
                    {/* 속지 이미지 */}
                    <div>
                      <p className="text-xs font-medium text-gray-600 mb-2">속지 이미지</p>
                      <button
                          onClick={() => setSelectedImage(1)}
                          className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                              selectedImage === 1 ? 'border-blue-500 shadow-md' : 'border-gray-200 hover:border-gray-300'
                          }`}
                      >
                        <LazyImage
                            src={productDetail.inlineFile.fileFullUrl}
                            alt="inline"
                            className="w-full h-full object-cover"
                        />
                      </button>
                    </div>

                    {/* 맺음말 이미지 */}
                    {productDetail.endFile && (
                            <div>
                              <p className="text-xs font-medium text-gray-600 mb-2">맺음말 이미지</p>
                              <button
                                  onClick={() => setSelectedImage(2)}
                                  className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                                      selectedImage === 2 ? 'border-blue-500 shadow-md' : 'border-gray-200 hover:border-gray-300'
                                  }`}
                              >
                                <LazyImage
                                    src={productDetail.endFile.fileFullUrl}
                                    alt="inline"
                                    className="w-full h-full object-cover"
                                />
                              </button>
                            </div>
                    )}
                  </div>

                  {/* 상세 이미지 */}
                  {productDetail.detailFiles && productDetail.detailFiles.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-gray-600 mb-1.5">
                        상세 이미지 ({productDetail.detailFiles.length}개)
                      </p>
                      <div className="flex gap-2 overflow-x-auto pb-1">
                        {productDetail.detailFiles.slice(0, 5).map((file, index) => (
                          <button
                            key={file.id}
                            onClick={() => setSelectedImage(index + 3)}
                            className={`flex-shrink-0 w-16 h-16 rounded overflow-hidden border-2 transition-all ${
                              selectedImage === index + 3 ? 'border-blue-500 shadow-md' : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <LazyImage
                              src={file.fileFullUrl}
                              alt={`detail-${index}`}
                              className="w-full h-full object-cover"
                            />
                          </button>
                        ))}
                        {productDetail.detailFiles.length > 5 && (
                          <div className="flex-shrink-0 w-14 h-14 rounded border-2 border-gray-200 flex items-center justify-center bg-gray-50 text-gray-500 text-xs">
                            +{productDetail.detailFiles.length - 5}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
          </div>

            {/* 정보 영역 */}
            <div className="bg-white rounded-lg p-5">
            {/* 상품 기본 정보 */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <h1 className="text-xl font-bold text-gray-900">
                    {productDetail?.productName || <div className="h-7 w-3/4 animate-pulse bg-gray-200 rounded"></div>}
                  </h1>
                  {productDetail?.confirmYn === 'Y' && (
                    <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded">상품 활성화</span>
                  )}
                  {role === 'ROLE_ADMIN' && productDetail && (
                    <div className="flex items-center gap-2">
                      <label
                        className="flex items-center gap-1.5 cursor-pointer px-2 py-1 rounded transition-colors hover:bg-gray-50"
                        title="가맹점에 상품 노출 여부를 설정합니다"
                      >
                        <input
                          type="checkbox"
                          checked={productDetail.displayYn === 'Y'}
                          disabled={isUpdatingDisplay}
                          onChange={async (e) => {
                            const newDisplayYn = e.target.checked ? 'Y' : 'N';

                            setIsUpdatingDisplay(true);
                            try {
                              await updateProduct(productDetail.id, {
                                displayYn: newDisplayYn
                              });
                              await getProductDetail(productDetail.id);
                              toast.success(`가맹점 노출이 ${e.target.checked ? '활성화' : '비활성화'} 되었습니다.`);
                            } catch (error) {
                              toast.error('가맹점 노출 상태 변경에 실패했습니다.');
                            } finally {
                              setIsUpdatingDisplay(false);
                            }
                          }}
                          className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                        />
                        <FontAwesomeIcon
                          icon={faEye}
                          className={`text-xs ${productDetail.displayYn === 'Y' ? 'text-blue-600' : 'text-gray-400'}`}
                        />
                        <span className={`text-xs font-medium ${productDetail.displayYn === 'Y' ? 'text-blue-700' : 'text-gray-600'}`}>
                          가맹점 노출
                        </span>
                      </label>
                    </div>
                  )}
                </div>
                <div className="relative flex items-center gap-2 flex-shrink-0">
                  <button
                      onClick={handlePreview}
                      className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors text-sm font-medium whitespace-nowrap"
                  >상품 샘플
                  </button>
                  {/* 말풍선 툴팁 */}
                  <div className="absolute -top-10 right-0 z-10">
                    <div className="relative bg-red-50 border border-red-200 text-red-600 text-sm px-3 py-1.5 rounded-lg whitespace-nowrap shadow-sm">
                      '사주톡톡' 실제 판매 상품 샘플
                    </div>
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-600">
                카테고리: {productDetail?.categoryName || '-'}
              </p>
            </div>
            
            {/* 가격 정보 */}
            <div className="mb-4 p-3 bg-gray-50 rounded">
              <div className="flex items-center gap-2 mb-1">
                <FontAwesomeIcon icon={faWonSign} className="text-gray-600 text-sm" />
                <span className="text-sm font-medium text-gray-700">판매가</span>
              </div>
              <PDPrice />
            </div>
            
            {/* 상품 상세 정보 */}
            <div className="space-y-4">
              <div>
                <h3 className="flex items-center text-sm font-semibold text-gray-700 mb-2">
                  <FontAwesomeIcon icon={faInfoCircle} className="mr-2 text-gray-400" />
                  기본 정보
                </h3>
                <div className="pl-6 space-y-2.5">
                  <div className="flex items-start text-sm min-h-[1.5rem]">
                    <span className="text-gray-500 w-24 flex-shrink-0">상품설명</span>
                    <span className="text-gray-700 flex-1">
                      {productDetail?.description || '-'}
                    </span>
                  </div>
                  <div className="flex items-center text-sm min-h-[1.5rem]">
                    <span className="text-gray-500 w-24 flex-shrink-0">서비스 형태</span>
                    <div className="flex-1">
                      <PDService />
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="flex items-center text-sm font-semibold text-gray-700 mb-2">
                  <FontAwesomeIcon icon={faFileAlt} className="mr-2 text-gray-400" />
                  제공 정보
                </h3>
                <div className="pl-6 space-y-2.5">
                  <div className="flex items-center text-sm min-h-[1.5rem]">
                    <span className="text-gray-500 w-24 flex-shrink-0">제공 형태</span>
                    <span className="text-gray-700 flex-1">PDF 파일</span>
                  </div>
                  <div className="flex items-center text-sm min-h-[1.5rem]">
                    <span className="text-gray-500 w-24 flex-shrink-0">전송 방법</span>
                    <span className="text-gray-700 flex-1">이메일</span>
                  </div>
                  <div className="flex items-center text-sm min-h-[1.5rem]">
                    <span className="text-gray-500 w-24 flex-shrink-0">소요 시간</span>
                    <span className="text-gray-700 flex-1">
                      <FontAwesomeIcon icon={faClock} className="mr-1 text-gray-400" />
                      결제 후 3영업일 이내
                    </span>
                  </div>
                </div>
              </div>
              
              {/* 등록 정보 */}
              {productDetail && (
                <div className="pt-3 border-t text-xs text-gray-500">
                  <div className="flex justify-between">
                    <span>등록일: {productDetail.createdAt ? new Date(productDetail.createdAt).toLocaleDateString() : '-'}</span>
                    <span>수정일: {productDetail.updatedAt ? new Date(productDetail.updatedAt).toLocaleDateString() : '-'}</span>
                  </div>
                </div>
              )}
            </div>
            
            {/* 관리자 버튼 영역 */}
            {role === 'ROLE_ADMIN' && (
              <div className="mt-4 pt-4 border-t">
                <PDButtonWrapper />
              </div>
            )}
              {(role === 'ROLE_USER' || role === 'ROLE_MARKETING') && productDetail && (
                  <div className = "mt-4 pt-4 border-t">
                    <button
                        onClick={() => navigate(`/update/product-image/${productDetail.id}`)}
                        className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors text-sm font-medium"
                    >
                      상품 수정
                    </button>
                  </div>
              )}
          </div>
        </div>
        
          {/* 상세 설명 이미지 */}
          {productDetail?.detailFiles && productDetail.detailFiles.length > 0 && (
            <div className="mt-4 bg-white rounded-lg p-5">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">속지 이미지</h3>
              <LazyImage
                  key={productDetail.inlineFile.id}
                  src={productDetail.inlineFile.fileFullUrl}
                  alt="detail"
                  className="w-full rounded border"
              />
              <h3 className="text-sm font-semibold text-gray-700 mb-3">상세 이미지</h3>
              <div className="space-y-2">
                {productDetail.detailFiles.map(file => (
                  <LazyImage 
                    key={file.id} 
                    src={file.fileFullUrl} 
                    alt="detail" 
                    className="w-full rounded border"
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* 미리보기 모달 */}
      {showPreviewModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg flex flex-col" style={{ width: '850px', height: '85vh' }}>
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-semibold">결과지 미리보기</h3>
              <button
                onClick={() => {
                  setShowPreviewModal(false);
                  setIsPdfLoading(false);
                  setPreviewPdfUrl('');
                }}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ✕
              </button>
            </div>
            <div className="flex-1 overflow-hidden relative">
              {isPdfLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
                  <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
                    <p className="text-gray-600">PDF를 불러오는 중입니다...</p>
                  </div>
                </div>
              )}
              <iframe
                  src={previewPdfUrl}
                  className="w-full h-full border-0"
                  title="PDF 미리보기"
                  onLoad={() => setIsPdfLoading(false)}
                  onError={() => {
                    setIsPdfLoading(false);
                    alert('PDF를 불러오는데 실패했습니다.');
                  }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetail;
